/**
* @summary - Component that Provides PDM Track functionality
* @autor vengateshwaran k
* @since 2024-05-30
*/
import { Box, Grid, Modal, Typography } from "@mui/material";
import { eachWeekOfInterval, endOfMonth, format, parseISO, startOfMonth } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import PpmPlannerRepository from '../../api/repositories/ppmPlannerRepository';
import { DefaultCard } from "../../assets/styled-components/card";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import SquareBoxRenderer from './SquareBoxRenderer'; // Import the custom cell renderer
import ActionButtons from "../commoncomponents/ActionButtons";
import { useNavigate } from 'react-router-dom';
import PremitiveMaintenanceRepository from "../../api/repositories/premitiveMaintenanceRepository";
import PageLoader from "../commoncomponents/PageLoader";
import PdmCalendarPdf from "../commoncomponents/PdmCalendarPdf";

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "1500px",
  height: "100%",
  maxHeight: "calc(100% - 64px)",
  bgcolor: "#fff",
  boxShadow: 24,
  borderRadius: "5px",
  p: 4,
  overflowY: "auto",
  overflowX: "hidden",
};

const PDMShowSchedule = ({ predictiveId, serviceType, facilityId, companyId, assetdeatils, taskCode,year }) => {
  const [gridApi, setGridApi] = useState({});
  const [getData, setGetData] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [allRowData, setAllRowData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const childRef = useRef(null);
  const [openModal, setOpenModal] = useState(false);

  const generateWeeklyColumns = (month, monthname) => {
    const year = new Date().getFullYear();
    const startDate = startOfMonth(new Date(year, month - 1));
    const endDate = endOfMonth(new Date(year, month - 1));
    const weeks = eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 1 });
    return weeks?.map((startOfWeek, index) =>
    ({
      // field: `${format(index ===0 ?startOfWeek+1:startOfWeek, 'MMM').toLowerCase()}_week${index + 1}`,
      field: `${monthname}_week${index + 1}`,
      headerName: `${index + 1}W`,
      flex: 2, // Adjust the flex value to increase the width
      minWidth: 100, // Adjust the minWidth value to increase the minimum width
      cellRenderer: SquareBoxRenderer, // Use the custom cell renderer
    }));
  };

  const colDefs1 = [
    { field: "assetNo", headerName: "Asset No", flex: 1, minWidth: 200, filter: "agTextColumnFilter", },
    { field: "assetdesc", headerName: "Asset Description", flex: 1, minWidth: 200, filter: "agTextColumnFilter", },
    { field: "taskcode", headerName: "Task Code", flex: 1, minWidth: 200, filter: "agTextColumnFilter", },
    { headerName: 'JAN', children: generateWeeklyColumns(1, 'jan') },
    { headerName: 'FEB', children: generateWeeklyColumns(2, 'feb') },
    { headerName: 'MAR', children: generateWeeklyColumns(3, 'mar') },
    { headerName: 'APR', children: generateWeeklyColumns(4, 'apr') },
    { headerName: 'MAY', children: generateWeeklyColumns(5, 'may') },
    { headerName: 'JUN', children: generateWeeklyColumns(6, 'jun') },
    { headerName: 'JUL', children: generateWeeklyColumns(7, 'jul') },
    { headerName: 'AUG', children: generateWeeklyColumns(8, 'aug') },
    { headerName: 'SEP', children: generateWeeklyColumns(9, 'sep') },
    { headerName: 'OCT', children: generateWeeklyColumns(10, 'oct') },
    { headerName: 'NOV', children: generateWeeklyColumns(11, 'nov') },
    { headerName: 'DEC', children: generateWeeklyColumns(12, 'dec') },
  ];
  const fetchOptions = async () => {
    try {
      setLoading(true);
      let schedule = null;
      if (serviceType?.value === 'FEMS' && predictiveId) {
        schedule = await PremitiveMaintenanceRepository.getPDMShowSchedule(predictiveId);
        if (schedule?.data?.length > 0) {
          const formattedData = schedule?.data?.map(item => {
            return {
              ...item
            };
          });
          setGetData(formattedData);
        }
      } else if (serviceType?.value === 'BEMS' && predictiveId) {
        schedule = await PremitiveMaintenanceRepository.getBemsPDMShowSchedule(predictiveId);
        if (schedule?.data?.length > 0) {
          const formattedData = schedule?.data?.map(item => {
            return {
              ...item,
            };
          });
          setGetData(formattedData);
        }
      }

    }
    catch (e) {
      console.log(e);
    }
    finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchOptions();

  }, [])

  useEffect(() => {
    if (serviceType?.value === 'FEMS') {
      if (getData !== null && getData.length > 0 && getData !== undefined) {
        const targetDates = getData?.map((item) => {
          const date = parseISO(item?.targetdate);
          const formattedDate = format(date, 'yyyy-MM-dd');
          return formattedDate
        });
        setAllRowData([{ assetNo: assetdeatils?.label, assetdesc: assetdeatils?.assetdesc, taskcode: taskCode, targetDates: targetDates.join(',') }]);
      }
    }
    else {
      if (getData !== null && getData.length > 0 && getData !== undefined) {
        const targetDates = getData?.map((item) => {
          const date = parseISO(item?.targetdate);
          const formattedDate = format(date, 'yyyy-MM-dd');
          return formattedDate
        });
        setAllRowData([{ assetNo: assetdeatils?.label, assetdesc: assetdeatils?.assetdesc, taskcode: taskCode, targetDates: targetDates.join(',') }]);
      }
    }
  }, [getData])

  const handleDateSubmit = async (inputAssetNo, inputDates, Row) => {
    if (!inputAssetNo || !inputDates) return [];
    const dates = inputDates.split(',')?.map(dateStr => parseISO(dateStr.trim()));
    const today = new Date();
    if (Row?.assetNo === inputAssetNo) {
      const updatedRow = { ...Row };
      dates.forEach(date => {
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        const startDate = startOfMonth(new Date(year, month - 1));
        const endDate = endOfMonth(new Date(year, month - 1));
        const weeks = eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 1 });
        let weekNumber = 0;
        weeks.forEach((weekStart, index) => {
          if (date >= weekStart && date < (index + 1 < weeks.length ? weeks[index + 1] : endDate)) {
            weekNumber = index + 1;
          }
        });

        const color = date > today ? 'linear-gradient(to right, red 25%, lightcoral 25%)' : 'linear-gradient(to right, green 25%, lightgreen 25%)'; // Determine the color based on date comparison
        updatedRow[`${format(date, 'MMM').toLowerCase()}_week${weekNumber}`] = { date, color }; // Save both date and color
      });
      return updatedRow;
    }
  }


  useEffect(() => {
    const fetchData = async () => {
      if (!allRowData) return;
      const results = await Promise.all(allRowData.map(async (item) => {
        const updatedRows = await handleDateSubmit(item?.assetNo, item?.targetDates, item);
        return updatedRows;
      }));
      // Flatten the results array as `handleDateSubmit` returns an array
      const flattenedResults = results.flat();
      setRowData(flattenedResults);
    };
    fetchData();
  }, [allRowData]);

  const handleGridRefresh = () => {
    fetchOptions();
  }

  const handleCancel = () => {
    navigate(-1, { replace: true });
  };

  const convertColumns = (inputColumns) => {
    const columns = [];

    inputColumns.forEach(column => {
      // Add assetno and assetdesc fields directly
      if (column.field) {
        columns.push({ field: column.field, header: "", subHeader: column.headerName });
      } else {
        // For months with child weeks, iterate over children
        column.children.forEach(week => {
          columns.push({ field: week.field, header: column.headerName, subHeader: week.headerName });
        });
      }
    });

    return columns;
  };


  const formatWeekData = (data) => {
    return data.map(item => {
      const weekData = Object.keys(item).reduce((acc, key) => {
        if (key.includes('week') && item[key]?.date) {
          const formattedDate = format(new Date(item[key].date), 'dd MMM yyyy - EEE');
          acc[key] = formattedDate;
        } else if (key.includes('week')) {
          acc[key] = ""; // Empty string for missing week data
        }
        return acc;
      }, {});

      return {
        assetNo: item.assetNo,
        assetdesc: item.assetdesc,
        taskcode: item.taskcode,
        ...weekData // Add dynamic week fields with formatted date
      };
    });
  };
  const cols = convertColumns(colDefs1)

  // Transform the asset data
  const data = formatWeekData(rowData);

  const handleDownloadPDF = async () => {
    // Open the modal and render the content
    setOpenModal(true);

    // Wait for the modal and component to fully load
    setTimeout(() => {
      if (childRef.current) {
        // Trigger the PDF download method after ensuring the component is loaded
        childRef.current.handleDownloadPDF();
      }

      // Close the modal after the PDF download is initiated
      setTimeout(() => {
        setOpenModal(false);
      }, 1000); // Adjust this timing as needed
    }, 500); // Adjust this timing to ensure the component is fully rendered
  };

  return (<>
    <PageLoader open={loading} />
    <DefaultCard>
      <Grid container spacing={2}>
        <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
            <Typography variant='h2'>Today,      {format(new Date(), 'dd MMM, yyyy')}</Typography>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'flex-end' }, marginTop: '10px' }}>
            <Box
              sx={{
                width: 15, // width of the box in pixels
                height: 15, // height of the box in pixels
                backgroundColor: 'green', // green background color
                marginRight: 1,
                marginTop: '1px'
              }}
            />
            <span>Completed</span>
          </Grid>
          <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'flex-end' }, marginTop: '10px' }}>
            <Box
              sx={{
                width: 15, // width of the box in pixels
                height: 15, // height of the box in pixels
                backgroundColor: 'red', // red background color
                marginRight: 1,
                marginTop: '1px'
              }}
            />
            <span>Pending</span>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
            <RefreshExportButtons
              customPdf={handleDownloadPDF}
              exportFunction={() => { }}
              exportData={rowData}
              title='PDM Show Schedule'
              refData={gridApi}
              handleRefresh={handleGridRefresh}
              formattedData={data}
              formattedHeaders={cols}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} md={12}>
          <CustomAgGrid
            ref={gridApi}
            setGridApi={setGridApi}
            rows={rowData}
            columns={colDefs1}
            disableToolTip={true}
            // disableFilter={true}
            frameworkComponents={{ squareBoxRenderer: SquareBoxRenderer }} // Register the custom cell renderer
          />
        </Grid>

        <Grid item xs={12} md={12}
          sx={{
            marginTop: "30px",
            display: "flex",
            justifyContent: "flex-end",
          }}>

          <ActionButtons
            handleCancel={handleCancel}
            isView={true}
          />
        </Grid>
      </Grid>
    </DefaultCard>
    <Modal
      open={openModal}
      sx={
        {
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
          },
          top: "-9999px",
          left: "-9999px",
          zIndex: "-1",
        }
      }
    >
      <Box sx={ModalStyle}>
        <PdmCalendarPdf
          ref={childRef}
          year={year?.label}
          headers={cols}
          data={data}
        />
      </Box>
    </Modal>
  </>
  );
}

export default PDMShowSchedule;
